import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  appColors,
  BackrsCard,
  ContentCompetency,
  ContentCompetencyProficiency,
  ContentNugget,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { groupNuggetsByCategory } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { NuggetList } from '@backrs_ui/grow-ui/src/components/Nuggets/NuggetList';
import { setCurrentDialogNugget } from '@backrs_ui/grow-ui/src/redux/contentNugget/contentNugget';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';

import { Box, Typography, Dialog } from '@mui/material';
import { SxProps, Theme } from '@mui/system';

import { EmptyCompetencyView } from '../atoms/EmptyCompetencyView';
import { CompetencyPageHeader } from '../molecules/ComptencyPageHeader';
import { ProficiencyLevelDialog } from '../molecules/ProficiencyLevelDialog';
import { ProficiencyLevelWidget } from '../molecules/ProficiencyLevelWidget';

const COMPETENCY_STRINGS = appStrings.modules.competency;

type CompetencyPageProps = Readonly<{
  categoryName: string;
  contentCompetency: ContentCompetency;
  nuggets: ContentNugget[];
  completedNuggetIds?: number[];
  currentProficiencyLevel: number;
  setProficiencyLevel: (competencyProficiency: ContentCompetencyProficiency) => void;
}>;

export const CompetencyPage = ({
  categoryName,
  contentCompetency,
  nuggets,
  completedNuggetIds,
  currentProficiencyLevel,
  setProficiencyLevel,
}: CompetencyPageProps) => {
  const [openProficiencyDialog, setOpenProficiencyDialog] = useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const secondColumnRef = useRef<HTMLDivElement>(null);
  const thirdColumnRef = useRef<HTMLDivElement>(null);

  const scrollToRef = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const handleOpeningNuggetDialog = (nugget: ContentNugget) => {
    dispatch(setCurrentDialogNugget(nugget));
  };

  // PROFICIENCY LEVEL DIALOG
  const handleProficiencyChange = (newProficiencyLevel: number) => {
    // type check just to make sure we're submitting a valid proficiency level
    const strictLevel: 0 | 1 | 2 | undefined = [0, 1, 2].includes(newProficiencyLevel)
      ? (newProficiencyLevel as 0 | 1 | 2)
      : undefined;

    if (strictLevel === undefined) {
      return;
    }

    setProficiencyLevel({
      content_competency_id: contentCompetency.id,
      proficiency: strictLevel,
    });
  };

  const proficiencyLevelDialog = (
    <Dialog
      open={openProficiencyDialog}
      onClose={() => setOpenProficiencyDialog(false)}
      maxWidth="md"
    >
      <ProficiencyLevelDialog
        currentProficiencyLevel={currentProficiencyLevel}
        handleProficiencyChange={handleProficiencyChange}
      />
    </Dialog>
  );

  const { learnAndExploreNuggets, realPeopleRealStoriesNuggets, growByDoingNuggets } =
    groupNuggetsByCategory(nuggets);

  function sortNuggetsByIncompleteFirst(nuggets: ContentNugget[]) {
    return nuggets.sort((a, b) => {
      if (completedNuggetIds?.includes(a.id) && !completedNuggetIds?.includes(b.id)) {
        return 1;
      }
      if (!completedNuggetIds?.includes(a.id) && completedNuggetIds?.includes(b.id)) {
        return -1;
      }

      return 0;
    });
  }

  return (
    <>
      {proficiencyLevelDialog}
      <Box sx={parentContainerStyle}>
        <CompetencyPageHeader
          contentCompetency={contentCompetency}
          categoryName={categoryName}
          handleNavigateToDashboard={() => navigate(APP_ROUTE.PROSPER)}
        />

        {nuggets.length ? (
          <Box sx={nuggetsProficiencyContainer}>
            {/* DESCRIPTION AND FIRST TWO COLUMN NUGGETS, THREE COLUMNS ON SMALL SCREENS */}
            <Box>
              <Box>
                <BackrsCard sx={descriptionContainerStyle}>
                  <Typography variant="body1" sx={descriptionStyle}>
                    {contentCompetency.description}
                  </Typography>
                </BackrsCard>
                <Box sx={smallScreenProficiencyWidgetContainer}>
                  <ProficiencyLevelWidget
                    currentProficiencyLevel={currentProficiencyLevel}
                    setOpenProficiencyDialog={setOpenProficiencyDialog}
                    nuggets={growByDoingNuggets}
                    completedNuggetIds={completedNuggetIds}
                    onNuggetClick={handleOpeningNuggetDialog}
                  />
                </Box>
              </Box>

              <Box sx={contentContainerStyle}>
                {/* LEARN & EXPLORE */}
                {learnAndExploreNuggets.length ? (
                  <Box sx={nuggetColumnContainer}>
                    <NuggetList
                      columnHeader={COMPETENCY_STRINGS.columnHeaders.learnAndExplore}
                      nuggets={sortNuggetsByIncompleteFirst(learnAndExploreNuggets)}
                      onClick={handleOpeningNuggetDialog}
                      handleScrollToNextColumn={() => scrollToRef(secondColumnRef)}
                    />
                  </Box>
                ) : null}

                {/* REAL PEOPLE, REAL STORIES */}
                {realPeopleRealStoriesNuggets.length ? (
                  <Box sx={nuggetColumnContainer}>
                    <NuggetList
                      columnHeader={COMPETENCY_STRINGS.columnHeaders.realPeopleRealStories}
                      nuggets={sortNuggetsByIncompleteFirst(realPeopleRealStoriesNuggets)}
                      onClick={handleOpeningNuggetDialog}
                      columnRef={secondColumnRef}
                      handleScrollToNextColumn={() => scrollToRef(thirdColumnRef)}
                    />
                  </Box>
                ) : null}

                {/* GROW BY DOING, WHEN SMALL SCREEN SIZE */}
                {growByDoingNuggets.length ? (
                  <Box
                    sx={{
                      ...nuggetColumnContainer,
                      '@media (min-width: 837px)': {
                        position: 'absolute !important',
                        top: '-9999px !important',
                        left: '-9999px !important',
                      },
                    }}
                  >
                    <NuggetList
                      columnHeader={COMPETENCY_STRINGS.columnHeaders.growByDoing}
                      nuggets={sortNuggetsByIncompleteFirst(growByDoingNuggets)}
                      onClick={handleOpeningNuggetDialog}
                      completedNuggetIds={completedNuggetIds}
                      columnRef={thirdColumnRef}
                    />
                  </Box>
                ) : null}
              </Box>
            </Box>
            <Box sx={largeScreenProficiencyWidgetContainer}>
              <ProficiencyLevelWidget
                currentProficiencyLevel={currentProficiencyLevel}
                setOpenProficiencyDialog={setOpenProficiencyDialog}
                nuggets={growByDoingNuggets}
                completedNuggetIds={completedNuggetIds}
                onNuggetClick={handleOpeningNuggetDialog}
              />
            </Box>
          </Box>
        ) : (
          <EmptyCompetencyView />
        )}
      </Box>
    </>
  );
};

const nuggetsProficiencyContainer: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
};

const parentContainerStyle: SxProps<Theme> = {
  backgroundColor: appColors.color.taupe2,
  px: 4,
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  overflowY: 'scroll',
  '@media (max-width: 500px)': {
    px: 2,
    boxSizing: 'border-box',
  },
  maxWidth: '1512px',
  alignSelf: 'center',
  margin: '0 auto',
};

const nuggetColumnContainer: SxProps<Theme> = {
  mr: 4,
  mb: 2,
  '@media (max-width: 837px)': {
    mr: 0,
  },
  '@media (max-width: 600px)': {
    mt: 5,
  },
};

const largeScreenProficiencyWidgetContainer: SxProps<Theme> = {
  pb: 5,
  width: '-webkit-fill-available',
  maxWidth: '500px',
  '@media (min-width: 1200px)': {
    width: '100%',
  },
  '@media (min-width: 950px)': {
    width: '100%',
  },
  '@media (max-width: 837px)': {
    position: 'absolute !important',
    top: '-9999px !important',
    left: '-9999px !important',
  },
};

const smallScreenProficiencyWidgetContainer: SxProps<Theme> = {
  mb: 2,
  '@media (min-width: 837px)': {
    position: 'absolute !important',
    top: '-9999px !important',
    left: '-9999px !important',
  },
};

const contentContainerStyle: SxProps<Theme> = {
  display: 'flex',
  mb: 3,
  mt: 1,
  zIndex: 10,
  // at smaller than 1000px, we want to stack the columns
  '@media (max-width: 1200px)': {
    flexDirection: 'column',
  },
};

const descriptionContainerStyle: SxProps<Theme> = {
  backgroundColor: appColors.color.taupe1,
  mb: 4,
  mr: 0,
  minHeight: '136px',
  '@media (min-width: 837px)': {
    mr: 4,
  },
  '@media (max-width: 600px)': {
    minHeight: 'auto',
  },
};

const descriptionStyle: SxProps<Theme> = {
  whiteSpace: 'pre-line',
  lineHeight: '24px',
  fontSize: '20px',
  '@media (max-width: 600px)': {
    fontSize: '16px',
  },
};
